/**
 * Flash HTML subset for TextField.htmlText (port of Ruffle's FormatSpans::from_html / to_html,
 * which were matched against Flash Player output).
 *
 * Supported tags: p (align), br, font (face, size [+/-], color, letterSpacing, kerning), b, i, u,
 * a (href, target), li, textformat (leftmargin, rightmargin, indent, blockindent, leading,
 * tabstops), span; unknown tags are ignored. Entities: &lt; &gt; &amp; &quot; &apos; &nbsp; &#N; &#xN;.
 */
import { Fmt, FormatSpans, Span, cloneFmt } from './spans';

type Token =
  | { kind: 'start'; name: string; attrs: Map<string, string>; selfClosing: boolean }
  | { kind: 'end'; name: string }
  | { kind: 'text'; text: string };

const ATTR_RE = /([^\s=/>"']+)\s*(?:=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;

function tokenize(html: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  const n = html.length;
  while (i < n) {
    const lt = html.indexOf('<', i);
    if (lt < 0) {
      out.push({ kind: 'text', text: html.slice(i) });
      break;
    }
    if (lt > i) out.push({ kind: 'text', text: html.slice(i, lt) });
    if (html.startsWith('<!--', lt)) {
      const endC = html.indexOf('-->', lt + 4);
      if (endC < 0) break;
      i = endC + 3;
      continue;
    }
    // Find the closing '>' honouring quoted attribute values.
    let j = lt + 1;
    let quote = '';
    while (j < n) {
      const c = html[j];
      if (quote) {
        if (c === quote) quote = '';
      } else if (c === '"' || c === "'") quote = c;
      else if (c === '>') break;
      j++;
    }
    if (j >= n) break; // unterminated tag: Flash/quick-xml stop parsing here
    let body = html.slice(lt + 1, j).trim();
    i = j + 1;
    if (body.startsWith('?') || body.startsWith('!')) continue;
    if (body.startsWith('/')) {
      const name = body.slice(1).trim().split(/\s+/)[0].toLowerCase();
      out.push({ kind: 'end', name });
      continue;
    }
    let selfClosing = false;
    if (body.endsWith('/')) {
      selfClosing = true;
      body = body.slice(0, -1).trim();
    }
    const m = /^([^\s/>]+)/.exec(body);
    if (!m) continue;
    const name = m[1].toLowerCase();
    const attrs = new Map<string, string>();
    const rest = body.slice(m[1].length);
    ATTR_RE.lastIndex = 0;
    let am: RegExpExecArray | null;
    while ((am = ATTR_RE.exec(rest))) {
      const key = am[1].toLowerCase();
      const val = am[3] ?? am[4] ?? am[5] ?? '';
      if (!attrs.has(key)) attrs.set(key, decodeEntities(val));
    }
    out.push({ kind: 'start', name, attrs, selfClosing });
    if (selfClosing) out.push({ kind: 'end', name });
  }
  return out;
}

const NAMED_ENTITIES: Record<string, string> = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'", nbsp: ' ' };

export function decodeEntities(s: string): string {
  if (s.indexOf('&') < 0) return s;
  return s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, (whole, ent: string) => {
    if (ent[0] === '#') {
      const code = ent[1] === 'x' || ent[1] === 'X' ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10);
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return whole;
      return String.fromCodePoint(code);
    }
    const v = NAMED_ENTITIES[ent.toLowerCase()];
    return v ?? whole;
  });
}

function isNewline(c: string): boolean {
  return c === '\r' || c === '\n';
}

/**
 * Parse Flash HTML into format spans. `multiline=false` ignores <br>, </p> and <li> line breaks
 * (literal newlines in text are kept), like Flash for single-line fields.
 */
export function parseHtml(html: string, defaultFormat: Fmt, multiline: boolean): FormatSpans {
  const formatStack: Fmt[] = [cloneFmt(defaultFormat)];
  let text = '';
  const spans: Span[] = [];
  const opened: string[] = [];
  let pOpen = false;
  let lastClosedFont: Pick<Fmt, 'font' | 'size' | 'color' | 'letterSpacing' | 'kerning'> | null = null;
  const top = () => formatStack[formatStack.length - 1];

  for (const tok of tokenize(html)) {
    if (tok.kind === 'start') {
      const attr = (k: string) => tok.attrs.get(k);
      let format = cloneFmt(top());
      switch (tok.name) {
        case 'br':
          if (multiline) {
            text += '\r';
            spans.push({ len: 1, fmt: cloneFmt(format) });
          }
          continue; // not pushed
        case 'sbr':
          text += '\r';
          spans.push({ len: 1, fmt: cloneFmt(format) });
          continue;
        case 'p': {
          pOpen = true;
          const align = attr('align');
          if (align !== undefined) {
            const a = align.toLowerCase();
            if (a === 'left' || a === 'center' || a === 'right' || a === 'justify') format.align = a;
          }
          break;
        }
        case 'a': {
          const href = attr('href');
          if (href !== undefined) format.url = href;
          const target = attr('target');
          if (target !== undefined) format.target = target;
          break;
        }
        case 'font': {
          const face = attr('face');
          if (face !== undefined) format.font = face;
          const sizeAttr = attr('size');
          if (sizeAttr !== undefined) {
            let prefix = '';
            let s = sizeAttr;
            if (s.startsWith('+') || s.startsWith('-')) {
              prefix = s[0];
              s = s.slice(1);
            }
            const m = /^[0-9]*/.exec(s);
            const digits = m ? m[0] : '';
            if (digits.length) {
              let size = parseInt(digits, 10);
              if (prefix === '+') size = format.size + size;
              else if (prefix === '-') size = format.size - size;
              format.size = Math.min(127, Math.max(1, size));
            }
          }
          const color = attr('color');
          if (color !== undefined && color.startsWith('#')) {
            const hex = color.slice(1).trimStart();
            let end = 0;
            while (end < hex.length && /[0-9a-fA-F]/.test(hex[end])) end++;
            const start = Math.max(0, end - 6);
            if (end > start) format.color = parseInt(hex.slice(start, end), 16) & 0xffffff;
          }
          const ls = attr('letterspacing');
          if (ls !== undefined) {
            const v = Number(ls);
            if (Number.isFinite(v)) format.letterSpacing = v;
          }
          const kerning = attr('kerning');
          if (kerning === '1') format.kerning = true;
          else if (kerning === '0') format.kerning = false;
          break;
        }
        case 'b':
          format.bold = true;
          break;
        case 'i':
          format.italic = true;
          break;
        case 'u':
          format.underline = true;
          break;
        case 'li': {
          const lastNl = text.length > 0 && text[text.length - 1] === '\r';
          if (multiline && !lastNl && text.length > 0) {
            text += '\r';
            spans.push({ len: 1, fmt: cloneFmt(top()) });
          }
          format.bullet = true;
          break;
        }
        case 'textformat': {
          const num = (k: string) => {
            const v = attr(k);
            if (v === undefined) return undefined;
            const n = Number(v);
            return Number.isFinite(n) ? n : undefined;
          };
          const lm = num('leftmargin');
          if (lm !== undefined) format.leftMargin = lm;
          const rm = num('rightmargin');
          if (rm !== undefined) format.rightMargin = rm;
          const ind = num('indent');
          if (ind !== undefined) format.indent = ind;
          const bi = num('blockindent');
          if (bi !== undefined) format.blockIndent = bi;
          const lead = num('leading');
          if (lead !== undefined) format.leading = lead;
          const ts = attr('tabstops');
          if (ts !== undefined) format.tabStops = ts.split(',').map((x) => Number(x.trim())).filter((x) => Number.isFinite(x));
          break;
        }
        default:
          break;
      }
      opened.push(tok.name);
      formatStack.push(format);
    } else if (tok.kind === 'text') {
      if (!tok.text.length) continue;
      const t = decodeEntities(tok.text).replace(/\r\n|\r|\n/g, '\r');
      if (!t.length) continue;
      text += t;
      spans.push({ len: t.length, fmt: cloneFmt(top()) });
    } else {
      // End tag: ignore mismatched ends (Flash behaviour).
      if (!opened.length || opened[opened.length - 1] !== tok.name) continue;
      opened.pop();
      if (tok.name === 'br' || tok.name === 'sbr') continue;
      if (tok.name === 'li' && multiline) {
        text += '\r';
        spans.push({ len: 1, fmt: cloneFmt(top()) });
      } else if (tok.name === 'p' && multiline) {
        if (pOpen) {
          pOpen = false;
          text += '\r';
          const f = cloneFmt(top());
          f.bold = false;
          f.italic = false;
          f.underline = false;
          f.url = '';
          f.target = '';
          const fontSrc = lastClosedFont ?? defaultFormat;
          f.font = fontSrc.font;
          f.size = fontSrc.size;
          f.color = fontSrc.color;
          f.letterSpacing = fontSrc.letterSpacing;
          f.kerning = fontSrc.kerning;
          spans.push({ len: 1, fmt: f });
        }
      } else if (tok.name === 'font') {
        const tf = top();
        lastClosedFont = { font: tf.font, size: tf.size, color: tf.color, letterSpacing: tf.letterSpacing, kerning: tf.kerning };
      }
      formatStack.pop();
    }
  }
  const fs = new FormatSpans(text, spans);
  fs.normalize();
  return fs;
}

// ---------------------------------------------------------------- serializer (Flash-like htmlText)

enum Tag {
  Textformat = 0,
  P = 1,
  Li = 2,
  Font = 3,
  A = 4,
  B = 5,
  I = 6,
  U = 7,
}

interface FontKey {
  font: string;
  size: number;
  color: number;
  letterSpacing: number;
  kerning: boolean;
}

function fontKey(f: Fmt): FontKey {
  return { font: f.font, size: f.size, color: f.color, letterSpacing: f.letterSpacing, kerning: f.kerning };
}

function fontEq(a: FontKey, b: FontKey): boolean {
  return a.font === b.font && a.size === b.size && a.color === b.color && a.letterSpacing === b.letterSpacing && a.kerning === b.kerning;
}

function hex2(v: number): string {
  return v.toString(16).toUpperCase().padStart(2, '0');
}

function colorStr(c: number): string {
  return '#' + hex2((c >> 16) & 255) + hex2((c >> 8) & 255) + hex2(c & 255);
}

function escapeXml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&apos;'));
}

export function toHtml(fs: FormatSpans): string {
  if (!fs.text.length) return '';
  let result = '';
  const fontStack: FontKey[] = [];
  const openTags: Tag[] = [];
  let cur: Fmt = {
    font: '',
    size: 0,
    color: 0,
    bold: false,
    italic: false,
    underline: false,
    url: '',
    target: '',
    align: 'left',
    leftMargin: 0,
    rightMargin: 0,
    indent: 0,
    leading: 0,
    letterSpacing: 0,
    kerning: false,
    blockIndent: 0,
    bullet: false,
    tabStops: [],
  };

  const closeTag = (t: Tag) => {
    if (t === Tag.Font) {
      result += '</FONT>'.repeat(fontStack.length);
      fontStack.length = 0;
      return;
    }
    result += t === Tag.Textformat ? '</TEXTFORMAT>' : t === Tag.P ? '</P>' : t === Tag.Li ? '</LI>' : t === Tag.B ? '</B>' : t === Tag.I ? '</I>' : t === Tag.U ? '</U>' : '</A>';
  };
  const closeTagsTill = (t: Tag) => {
    while (openTags.length && openTags[openTags.length - 1] >= t) closeTag(openTags.pop() as Tag);
  };
  const openTag = (t: Tag) => {
    if (openTags.includes(t)) return;
    if (openTags.length && openTags[openTags.length - 1] > t) return;
    openTags.push(t);
    switch (t) {
      case Tag.Textformat: {
        result += '<TEXTFORMAT';
        if (cur.leftMargin !== 0) result += ` LEFTMARGIN="${cur.leftMargin}"`;
        if (cur.rightMargin !== 0) result += ` RIGHTMARGIN="${cur.rightMargin}"`;
        if (cur.indent !== 0) result += ` INDENT="${cur.indent}"`;
        if (cur.leading !== 0) result += ` LEADING="${cur.leading}"`;
        if (cur.blockIndent !== 0) result += ` BLOCKINDENT="${cur.blockIndent}"`;
        if (cur.tabStops.length) result += ` TABSTOPS="${cur.tabStops.join(',')}"`;
        result += '>';
        break;
      }
      case Tag.P:
        result += `<P ALIGN="${cur.align.toUpperCase()}">`;
        break;
      case Tag.Li:
        result += '<LI>';
        break;
      case Tag.A:
        result += `<A HREF="${cur.url}" TARGET="${cur.target}">`;
        break;
      case Tag.B:
        result += '<B>';
        break;
      case Tag.I:
        result += '<I>';
        break;
      case Tag.U:
        result += '<U>';
        break;
      default:
        break;
    }
  };
  const setFont = (f: FontKey) => {
    if (fontStack.length) {
      const last = fontStack[fontStack.length - 1];
      if (fontEq(last, f)) return;
      closeTagsTill(Tag.A);
      result += '<FONT';
      if (f.font !== last.font) result += ` FACE="${f.font}"`;
      if (f.size !== last.size) result += ` SIZE="${f.size}"`;
      if (f.color !== last.color) result += ` COLOR="${colorStr(f.color)}"`;
      if (f.letterSpacing !== last.letterSpacing) result += ` LETTERSPACING="${f.letterSpacing}"`;
      if (f.kerning !== last.kerning) result += ` KERNING="${f.kerning ? 1 : 0}"`;
      result += '>';
      fontStack.push(f);
    } else {
      closeTagsTill(Tag.A);
      result += `<FONT FACE="${f.font}" SIZE="${f.size}" COLOR="${colorStr(f.color)}" LETTERSPACING="${f.letterSpacing}" KERNING="${f.kerning ? 1 : 0}">`;
      fontStack.push(f);
      openTags.push(Tag.Font);
    }
  };
  const closeFontIfFeasible = (f: FontKey) => {
    const pos = fontStack.findIndex((x) => fontEq(x, f));
    if (pos < 0 || pos === fontStack.length - 1) return;
    closeTagsTill(Tag.A);
    result += '</FONT>'.repeat(fontStack.length - pos - 1);
    fontStack.length = pos + 1;
  };
  const setSpan = (span: Fmt) => {
    if (span.bold !== cur.bold || span.italic !== cur.italic || span.underline !== cur.underline) closeTagsTill(Tag.B);
    if (span.url !== cur.url) closeTagsTill(Tag.A);
    closeFontIfFeasible(fontKey(span));
    cur = span;
    if (cur.leftMargin !== 0 || cur.rightMargin !== 0 || cur.indent !== 0 || cur.leading !== 0 || cur.blockIndent !== 0 || cur.tabStops.length) openTag(Tag.Textformat);
    if (!openTags.includes(Tag.P) && !openTags.includes(Tag.Li)) openTag(cur.bullet ? Tag.Li : Tag.P);
    setFont(fontKey(cur));
    if (cur.url) openTag(Tag.A);
    if (cur.bold) openTag(Tag.B);
    if (cur.italic) openTag(Tag.I);
    if (cur.underline) openTag(Tag.U);
  };
  const pushLine = (line: string) => {
    if (line.length) result += escapeXml(line);
  };
  const pushText = (t: string) => {
    const endsNl = t.length > 0 && isNewline(t[t.length - 1]);
    const body = endsNl ? t.slice(0, -1) : t;
    const lines = body.split(/[\r\n]/);
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) {
        closeTagsTill(Tag.Textformat);
        setSpan(cur);
      }
      pushLine(lines[i]);
    }
    if (endsNl) closeTagsTill(Tag.Textformat);
  };

  for (const [s, e, f] of fs.iter()) {
    setSpan(f);
    pushText(fs.text.slice(s, e));
  }
  closeTagsTill(Tag.Textformat);
  return result;
}
